import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { useQuery } from '@tanstack/react-query';
import { projectFolderFilesQuery } from '@/entities/project-folder';
import { useSetFolderThumbnail } from '@/features/manage-project-folders';

import { ThumbnailPicker } from '../ThumbnailPicker';

interface FolderThumbnailPickerProps {
  projectId: string;
  folder: FolderResponseDto;
  open: boolean;
  onClose: () => void;
}

/**
 * Wires the generic ThumbnailPicker to the folder thumbnail domain: fetches the
 * files in the folder, wraps the mutation, and closes on success.
 */
export function FolderThumbnailPicker(props: FolderThumbnailPickerProps) {
  const { projectId, folder, open, onClose } = props;

  const filesQuery = useQuery({
    ...projectFolderFilesQuery(projectId, folder.id),
    enabled: open,
  });

  const mutation = useSetFolderThumbnail({
    projectId,
    folderId: folder.id,
    onSuccess: onClose,
  });

  return (
    <ThumbnailPicker
      files={filesQuery.data?.items ?? []}
      currentOverrideFileId={folder.thumbnailOverrideFileId ?? null}
      open={open}
      onClose={onClose}
      isLoading={filesQuery.isLoading}
      isSaving={mutation.isPending}
      onSave={(fileId) => mutation.mutate(fileId)}
      onClear={() => mutation.mutate(null)}
    />
  );
}
